// dashboard-table.base.ts
import { AfterViewInit, ChangeDetectorRef, DestroyRef, Injectable, Input, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { FormControl, FormGroup } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { catchError, debounceTime, distinctUntilChanged, of, startWith, switchMap, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { Filter, JwtPayload, PaginateQuery, PaginateResult } from '../../models';
import { ErrorHandlingService, TokenService } from '../../services';
import { DashboardService, DashboardFilter, QuoteWidgetSummaryModel } from '../../../services/dashboard.service';
import { DashboardMySalesService } from '../../../services/dashboard-my-sales.service';
import { DashboardDataService, DashboardMethod } from '../../../services/dashboard-data-service.interface';

@Injectable({
  providedIn: 'root',
})
export abstract class DashboardList implements AfterViewInit {
  @Input() manySelectable: boolean = false;
  @Input() singleSelectable: boolean = false;

  @Input() endpointMethod!: DashboardMethod;

  @Input() buildNavigationUrl?: (row: any) => string;

  @Input() filtersForm?: FormGroup; // expects controls: startDate, endDate, search (optional)

  dataGrid!: Table;
  selectedRows: any[] = [];
  selectedRowsData: any[] = [];

  fields: string[] = [];
  fieldsReport: any[] = [];

  paginateQuery = { skip: 0, take: 10, orderBy: {} } as PaginateQuery;
  sortField!: string;

  popupVisibleConfirm = false;
  popupVisibleMessage = false;

  searchControl = new FormControl();
  formGroup!: FormGroup;

  data: PaginateResult = { list: [], total: 0 };
  rowsPerPage = [10, 20, 50];
  lastEvent: TableLazyLoadEvent = {};
  loading = signal(true);
  skip = 0;

  protected destroyRef = inject(DestroyRef);
  protected errorHandlingService = inject(ErrorHandlingService);
  protected changeDetector = inject(ChangeDetectorRef);
  protected toastr = inject(ToastrService);
  protected tokenService = inject(TokenService);
  protected messageService = inject(MessageService);
  protected confirmationService = inject(ConfirmationService);
  protected router = inject(Router);
  protected dashboardService = inject(DashboardService);
  protected dashboardMySalesService = inject(DashboardMySalesService);

  jwtPayload = {} as JwtPayload;

  ngOnInit() {
    this.getJwtPayload();
  }

  ngAfterViewInit(): void {
    this.search();
  }

  public load(event: TableLazyLoadEvent) {
    this.loading.set(true);
    this.lastEvent = event;

    const filter = this.buildFilter(event);

    return this.invokeDashboard(this.endpointMethod, filter)
      .pipe(
        catchError((error) => {
          this.loading.set(false);
          return this.errorHandlingService.handleError(error, 'Error retrieving data.');
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.data = result;
        this.replaceNullOrEmptyValues(this.data);
        this.loading.set(false);
        this.changeDetector.markForCheck();
      });
  }

  protected buildFilter(event: TableLazyLoadEvent): DashboardFilter {
    const { first, sortField, sortOrder, rows } = event;

    const fallbackSort = Object.keys(this.paginateQuery.orderBy ?? {})[0] ?? Object.keys(this.defaultSort())[0];
    const sortKey = (sortField as string) ?? fallbackSort;
    const sortDir = sortOrder === 1 ? 'asc' : 'desc';
    const orderBy: string[] = sortKey ? [`${sortKey},${sortDir}`] : [];

    this.sortField = sortKey;
    // Update paginateQuery.orderBy to match the current sort state
    this.paginateQuery.orderBy = { [sortKey]: sortDir.toUpperCase() === 'ASC' ? 'ASC' : 'DESC' } as any;
    this.skip = first ?? 0;
    this.paginateQuery.skip = first ?? 0;
    this.paginateQuery.take = rows ?? this.paginateQuery.take ?? 10;

    const filter: DashboardFilter = {
      skip: this.skip,
      take: rows ?? this.paginateQuery.take ?? 10,
      search: this.resolveSearchValue(),
      orderBy,
    };

    const { startDate, endDate } = this.resolveDateRange();
    if (startDate) { filter.startDate = startDate; }
    if (endDate) { filter.endDate = endDate; }

    const ownerIds = this.resolveOwnerIds();
    if (ownerIds && ownerIds.length > 0) { filter.ownerIds = ownerIds; }

    return filter;
  }

  protected invokeDashboard(method: DashboardMethod, filter: DashboardFilter): Observable<PaginateResult> {
    return this.getDashboardDataService()[method](filter);
  }

  protected getSummary(filter: DashboardFilter): Observable<QuoteWidgetSummaryModel> {
    return this.getDashboardDataService().getQuotesSummary(filter);
  }

  edit(row: any, idField: string): void {
    if (!this.buildNavigationUrl) {
      return;
    }
    const url = this.buildNavigationUrl(row);
    if (url) {
      this.router.navigateByUrl(url);
    }
  }

  search(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
        switchMap((value) => {
          if (this.filtersForm && this.filtersForm.get('search')) {
            this.filtersForm.get('search')!.setValue(value, { emitEvent: false });
          } else {
            this.paginateQuery.search = value;
          }
          return of(this.fetchData());
        }),
      )
      .subscribe();

    if (this.filtersForm) {
      this.filtersForm.valueChanges
        .pipe(
          startWith(this.filtersForm.value),
          debounceTime(150),
          takeUntilDestroyed(this.destroyRef),
          switchMap(() => of(this.fetchData())),
        )
        .subscribe();
    }
  }

  fetchData() {
    const sortKey = this.sortField ?? Object.keys(this.paginateQuery.orderBy ?? {})[0];
    const sortOrder = sortKey && this.paginateQuery.orderBy ? (this.paginateQuery.orderBy[sortKey] === 'ASC' ? 1 : -1) : 1;
    return this.load({
      first: this.skip,
      rows: this.paginateQuery.take,
      filters: this.paginateQuery.filters as any,
      sortOrder,
      sortField: sortKey,
    });
  }

  refreshFilter(filters: Filter[]) {
    this.paginateQuery.filters = filters;
    this.load(this.lastEvent);
  }

  replaceNullOrEmptyValues(data: PaginateResult) {
    data.list?.forEach((item) => {
      Object.keys(item).forEach((key) => {
        item[key] = this.getDefaultValue(item[key]);
      });
    });
  }

  getDefaultValue(value: any) {
    if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '' && !Array.isArray(value))) {
      return '--';
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '--';
      }
      const hasObjectItems = value.some((item) => item !== null && typeof item === 'object');
      return hasObjectItems ? value : value.join(', ');
    }
    return value;
  }

  protected defaultSort(): { [key: string]: string } {
    return { createdOn: 'DESC' };
  }

  getDefaultFiltersToReport() {
    const filters: any[] = [];
    for (const item of this.fieldsReport) {
      const value = this.formGroup?.controls?.[item.key]?.value;
      if (value) {
        filters.push({
          field: item.filed,
          operation: item.operation,
          value,
          condition: 'AND',
        });
      }
    }
    return filters as any;
  }

  getDefaultFilters = (searchTxt: string, fields: string[], operation = 1, condition = 'OR'): any[] => {
    const filters: any[] = [];
    for (const field of fields) {
      filters.push({
        field,
        operation,
        value: searchTxt,
        condition,
      });
    }
    return filters;
  };

  getJwtPayload() {
    this.jwtPayload = this.tokenService.getJwtPayload();
  }

  filtersChanges() {
    if (!this.formGroup) {
      return;
    }
    this.formGroup.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          this.paginateQuery.filters = this.getDefaultFiltersToReport() as any;
          return of(this.fetchData());
        }),
      )
      .subscribe();
  }

  private resolveSearchValue(): string | undefined {
    if (this.filtersForm && this.filtersForm.get('search')) {
      const v = this.filtersForm.get('search')!.value;
      if (v !== null && v !== undefined && String(v).trim() !== '') {
        return String(v);
      } else {
        return undefined;
      }
    } else {
      return this.paginateQuery.search;
    }
  }

  private resolveDateRange(): { startDate?: string | Date; endDate?: string | Date } {
    if (!this.filtersForm) {
      return {};
    }
    const start = this.filtersForm.get('startDate')?.value;
    const end = this.filtersForm.get('endDate')?.value;

    const startDate = this.normalizeDateValue(start);
    const endDate = this.normalizeDateValue(end);

    const out: { startDate?: string | Date; endDate?: string | Date } = {};
    if (startDate) { out.startDate = startDate; }
    if (endDate) { out.endDate = endDate; }
    return out;
  }

  private resolveOwnerIds(): string[] | undefined {
    if (!this.filtersForm) return undefined;
    const value = this.filtersForm.get('ownerIds')?.value;
    if (!Array.isArray(value) || value.length === 0) return undefined;
    return value.filter((id): id is string => typeof id === 'string' && id.length > 0);
  }

  private normalizeDateValue(value: any): string | Date | undefined {
    if (!value) {
      return undefined;
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString();
    } else {
      return value;
    }
  }

  private shouldUseMySalesService(): boolean {
    return this.tokenService.isContributor() && !this.tokenService.isManagerOrAdmin();
  }

  private getDashboardDataService(): DashboardDataService {
    return this.shouldUseMySalesService() ? this.dashboardMySalesService : this.dashboardService;
  }
}
